/**
 * Alojamiento de una máquina virtual.
 *
 * No todas las VMs corren en un hipervisor de la empresa: un VPS de OVH o una
 * máquina de Azure no aparecen en `virt_hypervisors` y hasta ahora se pintaban
 * como «Sin hipervisor», que parece un dato que falta en vez de una decisión.
 *
 * El proveedor es una **marca**: el cliente delegado sí lo ve (L6 Ecosystem).
 * Lo que nunca ve es el hipervisor, el clúster, el rack ni la cuenta del dueño;
 * de eso se encarga el backend anulando esos campos (`hosting_hidden`).
 */

export const HOSTING_PROVIDERS = [
  { value: 'l6_ecosystem', label: 'L6 Ecosystem' },
  { value: 'azure', label: 'Microsoft Azure' },
  { value: 'aws', label: 'Amazon Web Services' },
  { value: 'gcp', label: 'Google Cloud' },
  { value: 'oci', label: 'Oracle Cloud' },
  { value: 'ovh', label: 'OVHcloud' },
  { value: 'hetzner', label: 'Hetzner' },
  { value: 'other', label: 'Otro proveedor' },
];

/** La plataforma propia: es la marca que ve el cliente delegado. */
export const HOSTING_PROVIDER_OWN = 'l6_ecosystem';

export function hostingProviderLabel(provider) {
  if (!provider) return null;
  const match = HOSTING_PROVIDERS.find((p) => p.value === provider);
  return match ? match.label : provider;
}

/** `true` si la máquina corre en la plataforma de un tercero (Azure, AWS, VPS…). */
export function isExternalHosting(vm) {
  const provider = vm && vm.hosting_provider;
  return Boolean(provider && provider !== HOSTING_PROVIDER_OWN);
}

/**
 * Texto de «Alojamiento», por orden:
 *   1. el hipervisor (si el que mira puede verlo)
 *   2. el clúster
 *   3. el proveedor declarado (Azure, AWS, VPS… o L6 Ecosystem)
 *   4. la empresa dueña, si es una máquina delegada sin plataforma declarada
 *   5. `null` → quien llama decide qué pintar («Sin hipervisor»)
 */
export function hostingLabel(vm) {
  if (!vm) return null;
  if (vm.hypervisor_name) return vm.hypervisor_name;
  if (vm.cluster_name) return vm.cluster_name;
  const provider = vm.hosting_provider_label || hostingProviderLabel(vm.hosting_provider);
  if (provider) return provider;
  if (vm.hosting_hidden && vm.company_name) return vm.company_name;
  return null;
}

/**
 * Una máquina entra en «Cloud / VPS» cuando su alojamiento **no** es un
 * hipervisor propio visible: las de un proveedor externo y las delegadas.
 * Las que no tienen ni hipervisor ni proveedor no son cloud: son un dato que
 * falta, y esas no se mezclan aquí.
 */
export function isCloudVm(vm) {
  if (!vm) return false;
  if (vm.hosting_hidden) return true;
  return isExternalHosting(vm);
}
